import React, { createContext, useContext, useState, useEffect } from 'react';

const CompanySettingsContext = createContext(null);

const DEFAULT_LOGO = '/logo.png';
const DEFAULT_FAVICON = '/favicon.ico';

// Same-origin guard: only accept logo / favicon paths that start with `/`
// or are inline `data:` URIs. Any cross-origin URL is replaced with the
// local default so a misconfigured admin value (or stale DB row) can never
// trigger CORS errors or broken images in the navbar.
const toSameOrigin = (url, fallback) => {
  if (!url || typeof url !== 'string') return fallback;
  const trimmed = url.trim();
  if (trimmed === '') return fallback;
  // Protocol-relative URLs ("//cdn.example/x.png") start with `/` but are
  // cross-origin — block them before the single-slash same-origin check.
  if (trimmed.startsWith('//')) return fallback;
  if (trimmed.startsWith('/') || trimmed.startsWith('data:')) return trimmed;
  // Anything else (http://, https://, file://, javascript:) → block.
  return fallback;
};

const DEFAULT_SETTINGS = {
  company_name: 'MoveAbroadIQ',
  website_name: 'MoveAbroadIQ',
  logo_url: DEFAULT_LOGO,
  favicon_url: DEFAULT_FAVICON,
  company_description: '',
  support_email: '',
  contact_email: '',
  default_meta_title: 'MoveAbroadIQ',
  default_meta_description: '',
};

// Update all favicon link elements in document head
const updateFavicon = (url) => {
  const faviconUrl = url || DEFAULT_FAVICON;
  
  // Remove any existing favicon links first
  const existingFavicons = document.querySelectorAll("link[rel*='icon']");
  existingFavicons.forEach(link => link.remove());
  
  // Create standard favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = faviconUrl;
  if (faviconUrl.endsWith('.png')) {
    link.type = 'image/png';
  } else if (faviconUrl.endsWith('.svg')) {
    link.type = 'image/svg+xml';
  }
  document.head.appendChild(link);

  // Create shortcut icon (for older browsers)
  const shortcutLink = document.createElement('link');
  shortcutLink.rel = 'shortcut icon';
  shortcutLink.href = faviconUrl;
  document.head.appendChild(shortcutLink);

  // Create apple-touch-icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = faviconUrl;
  document.head.appendChild(appleLink);
  
  // Create 32x32 icon
  const link32 = document.createElement('link');
  link32.rel = 'icon';
  link32.type = 'image/png';
  link32.sizes = '32x32';
  link32.href = faviconUrl;
  document.head.appendChild(link32);
  
  // Create 16x16 icon
  const link16 = document.createElement('link');
  link16.rel = 'icon';
  link16.type = 'image/png';
  link16.sizes = '16x16';
  link16.href = faviconUrl;
  document.head.appendChild(link16);

  console.log('Favicon updated to:', faviconUrl);
};

export function CompanySettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch company settings on mount. Uses the native `fetch` (no wrapper) +
  // `cache: 'no-store'` + a per-request `?_=<epochms>` cache-buster so any
  // intermediate HTTP / CDN cache is bypassed. Always spreads the response
  // into a new object so React sees a fresh reference and re-renders.
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const url = `/api/company-settings?_=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          console.log('[CompanySettings] fetched:', data);
          setSettings({ ...data });

          // Update favicon dynamically - use favicon_url if set, otherwise use logo_url, otherwise default
          const faviconToUse = data.favicon_url || data.logo_url || DEFAULT_FAVICON;
          updateFavicon(faviconToUse);
        } else {
          // API failed, use default favicon
          updateFavicon(DEFAULT_FAVICON);
        }
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
        // Use default favicon on error
        updateFavicon(DEFAULT_FAVICON);
      } finally {
        setIsLoading(false);
      }
    };

    // Set default favicon immediately, then update when settings load
    updateFavicon(DEFAULT_FAVICON);
    fetchSettings();
  }, []);

  // Method to refresh settings (called after admin updates). Same native-fetch
  // + no-store + cache-buster + object-spread pattern so the navbar flips to
  // the new logo on the very next render after an upload+save.
  const refreshSettings = async () => {
    try {
      const url = `/api/company-settings?_=${Date.now()}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        console.log('[CompanySettings] refreshed:', data);
        setSettings({ ...data });

        // Update favicon - use favicon_url if set, otherwise use logo_url, otherwise default
        const faviconToUse = data.favicon_url || data.logo_url || DEFAULT_FAVICON;
        updateFavicon(faviconToUse);
      }
    } catch (error) {
      console.error('Failed to refresh company settings:', error);
    }
  };

  // Same-origin sanitized paths (before cache-busting). These are what the
  // UI actually renders — external URLs returned by the API are replaced
  // with the local default so the navbar / favicon are always safe to load.
  const safeLogo = toSameOrigin(settings.logo_url, DEFAULT_LOGO);
  const safeFavicon = toSameOrigin(settings.favicon_url, DEFAULT_FAVICON);

  // Cache-bust the logo / favicon URLs dynamically using `settings.updated_at`
  // (epoch-ms). As soon as an admin save lands, the stamp flips → React sees
  // a brand-new `logoUrl` string → img `key={logoUrl}` below remounts → the
  // browser fetches the new asset. No useMemo / useRef caching anywhere.
  const updatedAtMs = settings?.updated_at
    ? new Date(settings.updated_at).getTime()
    : 0;
  const safeStamp = Number.isFinite(updatedAtMs) && updatedAtMs > 0
    ? updatedAtMs
    : Math.floor(Date.now() / 86400000);
  const _bust = (url) => {
    if (!url) return url;
    // data: URIs are inline — no cache-bust needed or meaningful.
    if (url.startsWith('data:')) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${safeStamp}`;
  };

  const logoUrl = settings?.logo_url ? _bust(safeLogo) : DEFAULT_LOGO;
  const faviconUrl = settings?.favicon_url ? _bust(safeFavicon) : DEFAULT_FAVICON;

  const value = {
    settings,
    isLoading,
    refreshSettings,
    // Convenience getters (logo/favicon are cache-busted + same-origin safe)
    logoUrl,
    faviconUrl,
    // `hasCustomLogo` lets consumers distinguish "admin uploaded a branded
    // logo" from "showing the default /logo.png". Used by Navbar to decide
    // whether to also render the company name next to the fallback mark.
    hasCustomLogo: safeLogo !== DEFAULT_LOGO,
    companyName: settings.company_name || settings.website_name || 'MoveAbroadIQ',
  };

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  );
}

export function useCompanySettings() {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    return {
      settings: DEFAULT_SETTINGS,
      isLoading: false,
      refreshSettings: () => {},
      logoUrl: DEFAULT_LOGO,
      faviconUrl: DEFAULT_FAVICON,
      hasCustomLogo: false,
      companyName: 'MoveAbroadIQ',
    };
  }
  return context;
}

export default CompanySettingsContext;
